import { SPACE_SELECTOR_TOGGLE_STAR } from '../../constants/actions';
import { currentUserId as getCurrentUserId } from '../../selectors/user';
import {
  getAllSpaces,
  getRecentlyViewedSpaceList,
  getSpaceById,
} from '@core/selectors/space/space.selectors';
import showToast from '../../toasts/operations/showToast.operation';
import { starSpace } from '../api';
import { STAR_SPACE_ERROR, UNSTAR_SPACE_ERROR } from './toasts';
import { analyticsTrackEvent } from '../../utils/analytics';
import {
  STAR_UNSTAR_MY_SPACE_PAGE_REMAINING,
  STAR_UNSTAR_MENU,
  TOTAL_STARRED_SPACES_AFTER_ACTION_KEY,
  SPACE_STARRED,
  SPACE_UNSTARRED,
  MY_SPACE_PAGE_RECENTLY_VIEWED_SECTION,
  MY_SPACE_PAGE_SEARCH_RESULT,
} from '@core/constants/analytics';
import { openCorrelationId, openTimestamp } from '../selectors';
import getNow from '../getNow';

const starSpaceSuccess = ({ spaceId, isStarred }) => ({
  type: SPACE_SELECTOR_TOGGLE_STAR,
  status: 'success',
  payload: { spaceId, isStarred },
});

const countOfStarSpaces = (state, isStarred) => {
  const allSpaces = getAllSpaces(state);
  const getRecentlyViewedSpaces = getRecentlyViewedSpaceList(state);
  let recentViewedSpacesFiltered = [];
  if (getRecentlyViewedSpaces.length) {
    recentViewedSpacesFiltered = getRecentlyViewedSpaces.filter(
      ({ spaceId }) => !allSpaces[spaceId]
    );
  }

  const totalNumberOfAssignedUnAssignedSpaces = [
    ...Object.values(allSpaces),
    ...recentViewedSpacesFiltered,
  ];
  const totalCount = totalNumberOfAssignedUnAssignedSpaces.reduce(
    (acc, v) => (v.isStarred ? acc + 1 : acc),
    0
  );
  return isStarred ? totalCount + 1 : totalCount - 1;
};

interface ToggleStarSpaceParams {
  spaceId: string;
  search?: string;
  sort?: string;
  showArchived?: boolean;
  category?: string;
  subCategory?: string;
  itemType?:
    | typeof STAR_UNSTAR_MY_SPACE_PAGE_REMAINING
    | typeof STAR_UNSTAR_MENU
    | typeof MY_SPACE_PAGE_RECENTLY_VIEWED_SECTION
    | typeof MY_SPACE_PAGE_SEARCH_RESULT;
}

const toggleStarSpace =
  ({
    spaceId,
    search,
    sort,
    showArchived,
    category,
    subCategory,
    itemType,
  }: ToggleStarSpaceParams) =>
  async (dispatch, getState) => {
    const state = getState();
    const currentUserId = getCurrentUserId(state);
    const { isStarred: isCurrentlyStarred } = getSpaceById(state, spaceId) || {
      isStarred: false,
    };

    const newIsStarred = !isCurrentlyStarred;

    // Optimistically update the UI
    dispatch(starSpaceSuccess({ spaceId, isStarred: newIsStarred }));

    try {
      await starSpace({
        userId: currentUserId,
        spaceId,
        isStarred: newIsStarred,
      });

      const eventName = newIsStarred ? SPACE_STARRED : SPACE_UNSTARRED;

      const starredSpacesCount = countOfStarSpaces(state, newIsStarred);
      analyticsTrackEvent(eventName, {
        itemType,
        userId: currentUserId,
        spaceId,
        selectorOpenFor: Math.round(getNow() - openTimestamp(state)),
        correlationId: openCorrelationId(state),
        search,
        sort,
        showArchived,
        categoriesEnabled: true,
        category,
        subCategory,
        [TOTAL_STARRED_SPACES_AFTER_ACTION_KEY]: starredSpacesCount,
      });
    } catch (error) {
      // Revert the optimistic update on error
      dispatch(starSpaceSuccess({ spaceId, isStarred: isCurrentlyStarred }));
      dispatch(
        showToast(!isCurrentlyStarred ? STAR_SPACE_ERROR : UNSTAR_SPACE_ERROR)
      );
    }
  };

export default toggleStarSpace;
